import axios from "@/utils/axios";
import axiosEmptyHeader from "axios";
import { compose, length, pathOr, propOr, tap } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE } from "@/constants/generic";
import { DefaultCategoryOptions, DefaultShareableOptions } from "@/constants/studySets";
import {
  ADD_STUDYSET,
  ADD_STUDYSETS,
  RESET_BUILDING_ASSIGNMENTS,
  RESET_BUILDING_STUDYSET,
  RESET_STUDYSETS,
  SET_CATEGORIES,
  SET_CREATED_ASSIGNMENTS,
  SET_CURRENT_STUDYSET,
  SET_DETAIL_READALOUD,
  UPDATE_BUILDING_ASSIGNMENTS,
  UPDATE_BUILDING_STUDYSET,
} from "../constants";
import { format } from "date-fns";
import { uploadFile, readFile } from "../../utils/uploader";

const initialState = () => ({
  // Object that represents that class that is being created
  buildingStudySet: {
    name: "",
    phrases: [],
    shareable: DefaultShareableOptions[0].value, //organization, group, public
    category: DefaultCategoryOptions[0].value,
  },
  imageUpload: null,
  newReadAloud: {
    name: "",
    shareable: DefaultShareableOptions[0].value, //organization, group, public
    text: "",
  },
  editReadAloud: {
    name: "",
    shareable: DefaultShareableOptions[0].value, //organization, group, public
    text: "",
    // warmUp: {},
  },
  warmUpInit: [],
  isLoadingEdit: false,
  editSuccess: false,
  cloneWarmUp: [],
  buildingAssignments: {
    studySets: [],
    assessments: [],
    assignments: [],
    readAloud: null,
  },
  // List of all classes for the default organization
  list: [],
  categories: ["IELTS", "TOEIC", "Culture", "Food", "Music and Movies", "Relationships", "Travel", "Work", "Warm-up"],
  // Selected class
  currentStudySet: {},
  createdAssignments: [],
  detailReadAloud: {},
  autoWarmUpStudySets: [],
  finalWarmUpStudySets: [],
  warmUpStudySets: {
    name: "Warm-up",
    shareable: "organization",
    phrases: [],
    category: "warm_up",
  },
  audioReadAloudLink: "",
  audioSpeed: 0.85,
});

const state = initialState();

const getters = {
  studySetsSize: compose(length, pathOr([], ["list", "results"])),
  getBuildingStudySet: ({ buildingStudySet, finalWarmUpStudySets }) => {
    return {
      ...buildingStudySet,
      phrases: finalWarmUpStudySets,
    };
  },
  getBuildingAssignments: propOr({}, "buildingAssignments"),
  getCreatedAssignments: propOr([], "createdAssignments"),
  getStudySetList: pathOr([], ["list", "results"]),
  getCurrentStudySet: propOr({}, "currentStudySet"),
  getStudySetOverview: pathOr({}, ["currentStudySet", "lessons"]),
  buildPagination(state) {
    const pageCounter = getters.studySetsSize(state);
    const numberPages = state.list.count / NUMBER_ELEMENTS_PER_PAGE;
    return {
      pageCounter,
      total: state.list.count || 0,
      next: state.list.next,
      previous: state.list.previous,
      numberPages: Math.floor(numberPages),
    };
  },
  // getCurrentStudySetId: path(["currentcurrentStudySetClass", "id"]),
  getStudySetCategories: propOr([], "categories"),
  getDetailReadAloud: propOr({}, "detailReadAloud"),
  userFillStudySets: ({ finalWarmUpStudySets }) => finalWarmUpStudySets.filter(item => !item.isAutoGenerated),
};

const mutations = {
  [ADD_STUDYSETS](state, studySets) {
    state.list = studySets;
  },
  [UPDATE_BUILDING_STUDYSET](state, updatedStudySets) {
    state.buildingStudySet = {
      ...state.buildingStudySet,
      ...updatedStudySets,
    };
  },
  [UPDATE_BUILDING_ASSIGNMENTS](state, updatedBuildingAssignments) {
    state.buildingAssignments = {
      ...state.buildingAssignments,
      ...updatedBuildingAssignments,
    };
  },
  [RESET_BUILDING_STUDYSET](state) {
    state.buildingStudySet = {
      name: "",
      phrases: [],
      shareable: DefaultShareableOptions[0].value, //organization, group, public
      category: DefaultCategoryOptions[0].value,
    };
  },
  [RESET_BUILDING_ASSIGNMENTS](state) {
    state.buildingAssignments = {
      assessments: [],
      studySets: [],
      assignments: [],
      readAloud: null,
    };
  },
  [ADD_STUDYSET](state, studySetObject) {
    state.list = [...state.list, studySetObject];
  },
  [SET_CURRENT_STUDYSET](state, currentStudySet) {
    state.currentStudySet = currentStudySet;
  },
  [SET_DETAIL_READALOUD](state, detailReadAloud) {
    state.detailReadAloud = detailReadAloud;
    state.warmUpInit = detailReadAloud.warm_up_ss.phrases;

    if (detailReadAloud.audio_url) {
      state.audioReadAloudLink = detailReadAloud.audio_url;
    } else {
      state.audioReadAloudLink = "";
    }
  },
  [RESET_STUDYSETS](state) {
    Object.assign(state, initialState());
  },
  [SET_CATEGORIES](state, categories) {
    state.categories = categories;
  },
  [SET_CREATED_ASSIGNMENTS](state, createdAssignments) {
    state.createdAssignments = createdAssignments;
  },
  updateNewReadAloud(state, newReadAloud) {
    state.newReadAloud = { ...newReadAloud };
  },
  updateEditReadAloud(state, editReadAloud) {
    state.editReadAloud = { ...state.editReadAloud, ...editReadAloud };
  },
  resetNewReadAloud() {
    state.newReadAloud = { name: "", shareable: "organization", text: "" };
  },
  resetEditReadAloud() {
    state.newReadAloud = { name: "", shareable: "organization", text: "" };
  },
  setFinalWarmUpStudySets(state, sets) {
    state.finalWarmUpStudySets = sets;
  },
  setAutoWarmUpStudySets(state, sets) {
    state.autoWarmUpStudySets = sets;
  },
  cloneWarmUp(state) {
    state.cloneWarmUp = JSON.parse(JSON.stringify(state.finalWarmUpStudySets));
  },
  changeCloneWarmUp(state, newList) {
    state.cloneWarmUp = newList;
  },
  changeEditSuccess(state, status) {
    state.editSuccess = status;
  },
  changeLoadingEdit(state, status) {
    state.isLoadingEdit = status;
  },
  changeImageUploadReadAloud(state, image) {
    state.imageUpload = image;
  },
  resetImageUpload(state) {
    state.imageUpload = null;
  },
  resetWarmUp(state) {
    state.autoWarmUpStudySets = [];
    state.finalWarmUpStudySets = [];
    state.warmUpStudySets = {
      name: "Warm-up",
      shareable: "organization",
      phrases: [],
      category: "warm_up",
    };
  },
  changeLinkAudio(state, link) {
    state.audioReadAloudLink = link;
  },
};

const actions = {
  updateNewReadAloud({ commit }, newReadAloud) {
    commit("updateNewReadAloud", newReadAloud);
  },
  resetNewReadAloud({ commit }) {
    commit("resetNewReadAloud");
  },
  updateEditReadAloud({ commit }, editReadAloud) {
    commit("updateEditReadAloud", editReadAloud);
  },
  resetEditReadAloud({ commit }) {
    commit("resetEditReadAloud");
  },
  updateBuildingStudySet({ commit }, updatedStudySet) {
    commit(UPDATE_BUILDING_STUDYSET, updatedStudySet);
  },
  updateBuildingAssignments({ commit }, updateAssignments) {
    commit(UPDATE_BUILDING_ASSIGNMENTS, updateAssignments);
  },
  createReadAloud({ commit, rootGetters, state }, { warmUpStudySetId, imageUrl }) {
    const loggedInUser = rootGetters["user/getUser"];
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const requestBody = {
      name: state.newReadAloud.name,
      shareable: state.newReadAloud.shareable,
      passage: state.newReadAloud.text,
      owner: loggedInUser.id,
      organization: organizationId,
      warm_up_id: warmUpStudySetId,
      image_url: imageUrl || null,
      audio_url: state.audioReadAloudLink || null,
      audio_speed: state.audioReadAloudLink ? state.audioSpeed : null,
    };
    return axios.post(`/v1/schools/reading-alouds/${organizationId}/`, requestBody).then(async response => {
      const createdStudySet = response.data;
      commit(SET_CURRENT_STUDYSET, createdStudySet);
      return response;
    });
  },
  async createWarmUpStudySets({ rootGetters, state }, phrases) {
    const loggedInUser = rootGetters["user/getUser"];
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const name = `${state.newReadAloud.name} - ${state.warmUpStudySets.name}`;
    const requestBody = {
      ...state.warmUpStudySets,
      name,
      shareable: state.newReadAloud.shareable,
      owner: loggedInUser.id,
      organization: organizationId,
      phrases,
    };
    return axios.post(`/v1/study-sets/${organizationId}/`, requestBody);
  },
  async uploadImageS3({ rootGetters, state, commit }, img) {
    try {
      if (!state.imageUpload) {
        return;
      }
      const fileImage = state.imageUpload.raw;
      const url = `/v1/schools/reading-alouds/image-upload?content_type=${fileImage.type}&file_size=${fileImage.size}`;

      const res = await axios.get(url);
      const presigned_url = res.data.presigned_url;

      const rawFile = await readFile(fileImage);

      await axiosEmptyHeader({
        method: "PUT",
        url: presigned_url,
        data: rawFile,
        headers: {
          "Content-Type": fileImage.type,
        },
      });

      return res.data;
    } catch (error) {
      // console.log(error);

      return error;
    }
  },
  resetBuildingStudySet({ commit }) {
    commit(RESET_BUILDING_STUDYSET);
  },
  resetBuildingAssignments({ commit }) {
    commit(RESET_BUILDING_ASSIGNMENTS);
  },
  getReadAloud({ dispatch, rootGetters, commit }, { readAloudId } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    try {
      commit("changeLoadingEdit", true);

      return axios.get(`/v1/schools/reading-alouds/${organizationId}/${readAloudId}/`).then(
        tap(response => {
          dispatch("setDetailReadAloud", response.data);
          commit("changeLoadingEdit", false);
          if (
            response.data &&
            response.data.warm_up_ss &&
            response.data.warm_up_ss.phrases &&
            response.data.warm_up_ss.phrases.length
          ) {
            dispatch(
              "setFinalWarmUpStudySets",
              response.data.warm_up_ss.phrases.map(el => ({ text: el, classes: "custom-tag", isAutoGenerated: true }))
            );
          }
          // dispatch("setFinalWarmUpStudySets", [])
        })
      );
    } catch (error) {
      commit("changeLoadingEdit", false);
    }
  },
  setDetailReadAloud({ commit }, detailReadAloud) {
    return commit(SET_DETAIL_READALOUD, detailReadAloud);
  },
  async updateStudySet({ commit, getters, rootGetters, state }) {
    const readAloudId = state.detailReadAloud.id;
    const loggedInUser = rootGetters["user/getUser"];
    const organizationId = rootGetters["organization/currentOrganizationId"];

    const reqUpdateWarmUp = {
      name: state.detailReadAloud.warm_up_ss.name,
      category: "warm_up",
      shareable: "organization",
      phrases: state.cloneWarmUp.map(el => el.text),
      owner: loggedInUser.id,
      organization: organizationId,
    };
    const warmUpId = state.detailReadAloud.warm_up_ss.id;
    const res = await axios.patch(`/v1/study-sets/${organizationId}/${warmUpId}/`, reqUpdateWarmUp);
    commit("changeEditSuccess", true);

    const requestBody = {
      name: state.editReadAloud.name,
      shareable: state.editReadAloud.shareable,
      passage: state.editReadAloud.text,
      owner: loggedInUser.id,
      organization: organizationId,
      audio_url: state.audioReadAloudLink || null,
      audio_speed: state.audioReadAloudLink ? state.audioSpeed : null,
    };
    
    return axios.patch(`/v1/schools/reading-alouds/${organizationId}/${readAloudId}/`, requestBody).then(response => {
      const newStudySet = {
        ...response.data,
      };
      commit(SET_CURRENT_STUDYSET, newStudySet);
      commit(RESET_BUILDING_STUDYSET);
      return response;
    });
  },

  updateReadAloudName({ commit, getters, rootGetters, state }, { readAloudId, payload } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.patch(`v1/schools/reading-alouds/${organizationId}/${readAloudId}/`, payload);
  },
  deleteReadAloud({ rootGetters }, readAloudId) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.delete(`/v1/schools/reading-alouds/${organizationId}/${readAloudId}/`);
  },
  reset({ commit }) {
    return commit(RESET_STUDYSETS);
  },
  createAssignments({ commit, getters, rootGetters }, assignment_library_id) {
    let buildingAssignments = getters["getBuildingAssignments"];
    const assignedClasses = buildingAssignments.assignments;
    const assignedStudySets = buildingAssignments.studySets;
    const assignedReadAloud = buildingAssignments.readAloud;
    const assessments = buildingAssignments.assessments;
    const assessmentIds = assessments.map(x => x.id);
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const payload_assignments = [];
    const studySetIds = assignedStudySets.map(x => x.id);
    const readAloud = assignedReadAloud ? [assignedReadAloud.id] : null;
    const now = format(Date.now(), "yyyy-MM-dd HH:mm");
    for (let i = 0; i < assignedClasses.length; i++) {
      const assignment = assignedClasses[i];
      const assignmentWithoutGroup = {
        name: assignment.name,
        // group: assignment.groupId,
        study_sets: studySetIds,
        read_aloud: readAloud,
        assessment_ids: assessmentIds,
        due_date: assignment.dueDate + " " + assignment.dueTime,
        start_date: assignment.startTime != "now" ? assignment.startDate + " " + assignment.startTime : now,
        reminder_before_day: assignment.reminderBeforeDay,
        notify_time: assignment.timeNotify,
        assignment_library_id: assignment_library_id,
        assign_now: assignment.assign_now,
      };
      for (let j = 0; j < assignment.groupIds.length; j++) {
        payload_assignments.push({ ...assignmentWithoutGroup, group: assignment.groupIds[j] });
      }
    }
    const requestBody = {
      assignments: payload_assignments,
    };
    return axios.post(`/v1/study-sets/${organizationId}/assignments`, requestBody).then(response => {
      const newAssignments = {
        ...response.data,
      };
      commit(SET_CREATED_ASSIGNMENTS, newAssignments);
      commit(RESET_BUILDING_ASSIGNMENTS);
      return response;
    });
  },
  checkReadAloudPermission({ rootGetters }, readAloudIds) {
    const requestBody = {
      ids: readAloudIds,
    };
    return axios.post("/v1/schools/reading-aloud/access-check/", requestBody).then(response => response.data);
  },
  async getWarmUpStudySets({ dispatch }, payload) {
    const data = await axios.post("/v1/schools/warm-up-study-set/", payload).then(response => response.data);
    const list = data.items.map(item => {
      return {
        ...item,
        classes: "custom-tag",
        isAutoGenerated: true,
      };
    });
    dispatch("setAutoWarmUpStudySets", list);
  },
  setAutoWarmUpStudySets({ commit }, items) {
    commit("setAutoWarmUpStudySets", items);
  },
  setFinalWarmUpStudySets({ commit }, items) {
    commit("setFinalWarmUpStudySets", items);
  },
  cloneWarmUpAction({ commit }) {
    commit("cloneWarmUp");
  },
  changeCloneWarmUp({ commit }, newList) {
    commit("changeCloneWarmUp", newList);
  },
  changeEditSuccess({ commit }, status) {
    commit("changeEditSuccess", status);
  },
  changeImageUploadReadAloud({ commit }, image) {
    commit("changeImageUploadReadAloud", image);
  },
  resetImageUpload({ commit }) {
    commit("resetImageUpload");
  },
  resetWarmUp({ commit }) {
    commit("resetWarmUp");
  },
  changeLinkAudio({ commit }, link) {
    commit("changeLinkAudio", link);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
